import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { View, Text, ScrollView, Pressable, TextInput, Platform, ActivityIndicator } from 'react-native'
import { useTheme } from '../../../theme/useTheme'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { useSnackbar } from '../../../shared/hooks/useSnackbar'
import { getAccountBrandingTabStyles, swatchBgStyle } from './AccountBrandingTab.styles'
import { BRAND_COLOR_SWATCHES } from '../../constants/accountAdmin.constants'
import { useAccountAdminCopy } from '../../hooks/useAccountAdminCopy'
import { BrandColorPalette } from '../BrandColorPalette/BrandColorPalette'
import { BrandingLogoSection } from '../BrandingLogoSection/BrandingLogoSection'
import type { B2bBrandingData } from '../../types/b2bAccount.types'
import { extractErrorMessage } from '../../../../helpers/apiClient.helper'
import { isValidHexColor } from '../../helpers/b2bAccount.helper'
import { BrandingPreviewSection } from '../BrandingPreviewSection/BrandingPreviewSection'
import { flushToPaint } from '../../helpers/flushToPaint.helper'

type BrandColorSlot = 'primary' | 'secondary'

interface AccountBrandingTabProps {
  accountId: string
  branding: B2bBrandingData | null
  isBrandingLoading?: boolean
  orgName: string | null
  onRefresh: () => Promise<void>
  patchBrandingColors: (input: {
    primaryColor?: string
    secondaryColor?: string
    useBannerBackground?: boolean
  }) => Promise<void>
  embedInSiteWebOuterScroll?: boolean
}

export const AccountBrandingTab: React.FC<AccountBrandingTabProps> = ({
  accountId,
  branding,
  isBrandingLoading = false,
  orgName,
  onRefresh,
  patchBrandingColors,
  embedInSiteWebOuterScroll = false,
}) => {
  const copy = useAccountAdminCopy()
  const theme = useTheme()
  const { showSnackbar } = useSnackbar()

  const [draftPrimary, setDraftPrimary] = useState('#5B21B6')
  const [draftSecondary, setDraftSecondary] = useState('#1E293B')
  const [lastValidPrimary, setLastValidPrimary] = useState('#5B21B6')
  const [lastValidSecondary, setLastValidSecondary] = useState('#1E293B')
  const [activeColorSlot, setActiveColorSlot] = useState<BrandColorSlot>('primary')
  const styles = useMemo(() => getAccountBrandingTabStyles(theme), [theme])
  const [savingColors, setSavingColors] = useState(false)

  const draftColor = activeColorSlot === 'primary' ? draftPrimary : draftSecondary
  const setDraftColor = useCallback(
    (value: string) => {
      if (activeColorSlot === 'primary') setDraftPrimary(value)
      else setDraftSecondary(value)
    },
    [activeColorSlot],
  )

  useEffect(() => {
    if (branding) {
      const p = branding.primaryColor ?? '#5B21B6'
      const s = branding.secondaryColor ?? '#1E293B'
      setDraftPrimary(p)
      setDraftSecondary(s)
      setLastValidPrimary(p)
      setLastValidSecondary(s)
    }
  }, [branding?.primaryColor, branding?.secondaryColor])

  useEffect(() => {
    if (isValidHexColor(draftPrimary)) setLastValidPrimary(draftPrimary)
  }, [draftPrimary])

  useEffect(() => {
    if (isValidHexColor(draftSecondary)) setLastValidSecondary(draftSecondary)
  }, [draftSecondary])

  const canSaveColors = isValidHexColor(draftPrimary) && isValidHexColor(draftSecondary)

  const handleSaveColors = useCallback(async () => {
    if (!canSaveColors) return
    await flushToPaint(() => setSavingColors(true))
    try {
      await patchBrandingColors({
        primaryColor: draftPrimary.trim(),
        secondaryColor: draftSecondary.trim(),
      })
      showSnackbar(copy.COLORS_SAVED, 'success')
    } catch (e) {
      showSnackbar(extractErrorMessage(e, 'Save failed'), 'error')
    } finally {
      setSavingColors(false)
    }
  }, [canSaveColors, draftPrimary, draftSecondary, patchBrandingColors, showSnackbar])

  const brandingBody = (
    <>
      <BrandingLogoSection
        accountId={accountId}
        branding={branding}
        onRefresh={onRefresh}
        onUseBannerBackgroundChange={async (next) => {
          await patchBrandingColors({ useBannerBackground: next })
        }}
        styles={styles}
        theme={theme}
      />

      <View
        style={styles.sectionDivider}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      />

      <View style={[styles.section, styles.sectionFlushBottom]}>
        <BrandingPreviewSection
          primaryColor={lastValidPrimary}
          secondaryColor={lastValidSecondary}
          logoUrl={branding?.logoCdnUrls[0] ?? null}
          bannerUrl={
            branding?.useBannerBackground && branding?.bannerCdnUrl ? branding.bannerCdnUrl : null
          }
          orgName={orgName}
          isLoading={isBrandingLoading}
          theme={theme}
        />
      </View>

      <View
        style={styles.sectionDivider}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      />

      <View style={styles.section}>
        <View style={styles.colorSlotRow}>
          <Pressable
            accessibilityRole="tab"
            accessibilityState={{ selected: activeColorSlot === 'primary' }}
            onPress={() => setActiveColorSlot('primary')}
            style={({ pressed }) => [
              styles.colorSlotTab,
              activeColorSlot === 'primary' && styles.colorSlotTabActive,
              pressed && styles.primaryButtonPressed,
            ]}
          >
            <Text
              style={[
                styles.colorSlotTabText,
                activeColorSlot === 'primary' && styles.colorSlotTabTextActive,
              ]}
            >
              {copy.PRIMARY}
            </Text>
          </Pressable>
          <Pressable
            accessibilityRole="tab"
            accessibilityState={{ selected: activeColorSlot === 'secondary' }}
            onPress={() => setActiveColorSlot('secondary')}
            style={({ pressed }) => [
              styles.colorSlotTab,
              activeColorSlot === 'secondary' && styles.colorSlotTabActive,
              pressed && styles.primaryButtonPressed,
            ]}
          >
            <Text
              style={[
                styles.colorSlotTabText,
                activeColorSlot === 'secondary' && styles.colorSlotTabTextActive,
              ]}
            >
              {copy.SECONDARY}
            </Text>
          </Pressable>
        </View>
        <Text style={styles.bodyText}>{copy.COLOR_PRESETS}</Text>
        <View style={styles.row}>
          {BRAND_COLOR_SWATCHES.map((hex) => (
            <Pressable
              key={hex}
              accessibilityRole="button"
              onPress={() => setDraftColor(hex)}
              style={({ pressed }) => [
                styles.swatch,
                swatchBgStyle(hex),
                draftColor.toUpperCase() === hex.toUpperCase() && styles.swatchSelected,
                pressed && styles.primaryButtonPressed,
              ]}
            />
          ))}
        </View>
        <BrandColorPalette
          value={draftColor}
          onColorChange={setDraftColor}
          label={copy.COLOR_PALETTE_LABEL}
          saturationBrightnessA11yLabel={copy.COLOR_PALETTE_SATURATION_A11Y}
          hueA11yLabel={copy.COLOR_PALETTE_HUE_A11Y}
        />
        <Text style={styles.bodyText}>{copy.CUSTOM_HEX_LABEL}</Text>
        <TextInput
          value={draftColor}
          onChangeText={setDraftColor}
          autoCapitalize="none"
          autoCorrect={false}
          placeholder={copy.HEX_PLACEHOLDER}
          placeholderTextColor={theme.gray[500]}
          style={styles.hexInput}
        />
        <Pressable
          accessibilityRole="button"
          disabled={savingColors || !canSaveColors}
          onPress={() => void handleSaveColors()}
          style={({ pressed, hovered }) => [
            styles.primaryButton,
            styles.saveColorsButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.primaryButtonPressed,
            hovered && Platform.OS === 'web' && styles.primaryButtonHover,
            (savingColors || !canSaveColors) && styles.primaryButtonDisabled,
          ]}
        >
          {savingColors ? <ActivityIndicator size="small" color={theme.white} style={{ marginRight: 6 }} /> : null}
          <Text style={styles.primaryButtonText}>{copy.SAVE_COLORS}</Text>
        </Pressable>
      </View>
    </>
  )

  if (embedInSiteWebOuterScroll) {
    return (
      <View style={[styles.embeddedBody, styles.scrollContent]}>{brandingBody}</View>
    )
  }

  return (
    <ScrollView
      style={styles.scrollView}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}
      keyboardShouldPersistTaps="handled"
    >
      {brandingBody}
    </ScrollView>
  )
}
